import { CustomStyleOverrideProps } from '@alpine-iq/ui-library';
import React from 'react';
import styled, { keyframes } from 'styled-components';

interface RadioProps extends React.HTMLAttributes<HTMLDivElement> {
	size?: number;
	selected: boolean;
	disabled?: boolean;
	defaultColor?: string;
	selectedColor?: string;
	baseOverrides?: CustomStyleOverrideProps;
}

const RadioContainer = styled.div<RadioProps>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: ${(props) => props.size}px;
	height: ${(props) => props.size}px;
	cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
	position: relative;
	opacity: ${(props) => (props.disabled ? 0.5 : 1)};
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const scaleIn = keyframes`
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
`;

const UnselectedSVG = styled.svg<{ size: number }>`
	width: ${(props) => props.size}px;
	height: ${(props) => props.size}px;
	animation: ${fadeIn} 0.1s ease-in-out;
`;

const SelectedSVG = styled.svg<{ size: number }>`
	width: ${(props) => props.size}px;
	height: ${(props) => props.size}px;
	animation: ${scaleIn} 0.1s ease-in-out;
`;

const HiddenInput = styled.input`
	position: absolute;
	opacity: 0;
	width: 100%;
	height: 100%;
	cursor: pointer;
`;

const Radio: React.FC<RadioProps> = ({ size = 24, selected, onClick, disabled, defaultColor, selectedColor, baseOverrides, ...props }) => {
	const onClickHandler = (e: React.MouseEvent<HTMLDivElement>) => {
		if (disabled) {
			e.preventDefault();
			return;
		}
		if (onClick) {
			onClick(e);
		}
	};

	const brandColor = baseOverrides?.brandColor ?? '#6262F5';
	const borderColor = baseOverrides?.borderColor ?? '#E2E6ED';

	return (
		<RadioContainer
			size={size}
			role="radio"
			aria-checked={selected}
			aria-disabled={disabled}
			disabled={disabled}
			onClick={onClickHandler}
			selected={selected}
			{...props}
		>
			<HiddenInput
				type="radio"
				checked={selected}
				onChange={() => {}}
				disabled={disabled}
			/>
			{!selected && (
				<UnselectedSVG
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 24 24"
					size={size}
					fill="none"
				>
					<circle
						cx="12"
						cy="12"
						r="11"
						fill="white"
						stroke={borderColor}
						strokeWidth="2"
					/>
				</UnselectedSVG>
			)}
			{selected && (
				<SelectedSVG
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 24 24"
					size={size}
					fill="none"
				>
					<path
						fillRule="evenodd"
						clipRule="evenodd"
						d="M0 12C0 18.6274 5.37258 24 12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12ZM23 12C23 18.0751 18.0751 23 12 23C5.92487 23 1 18.0751 1 12C1 5.92487 5.92487 1 12 1C18.0751 1 23 5.92487 23 12Z"
						fill={selectedColor ?? brandColor}
					/>
					<circle
						cx="12"
						cy="12"
						r="9"
						fill={selectedColor ?? brandColor}
					/>
				</SelectedSVG>
			)}
		</RadioContainer>
	);
};

export default Radio;
